import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { AgentTrace, MonitoringStats, AuditEntry } from '@/types/monitoring';
import { getAccessToken } from '@/utils/jwt';

export const monitoringApi = createApi({
  reducerPath: 'monitoringApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGENT_MCP_URL || '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Trace', 'Audit', 'Stats'],
  endpoints: (builder) => ({
    getStats: builder.query<MonitoringStats, undefined>({
      query: () => '/monitoring/metrics',
      transformResponse: (response: MonitoringStats & { success: boolean }) =>
        ({
          metrics: response.metrics,
          guardrails: response.guardrails,
          tools: response.tools,
          active_guardrails_count: response.active_guardrails_count,
          timestamp: response.timestamp,
        }) as MonitoringStats,
      providesTags: ['Stats'],
    }),
    listTraces: builder.query<AgentTrace[], { limit?: number; offset?: number }>({
      query: (params) => ({ url: '/monitoring/traces', params }),
      transformResponse: (response: { traces: AgentTrace[]; count: number }) => response.traces,
      providesTags: ['Trace'],
    }),
    getTrace: builder.query<AgentTrace, string>({
      query: (id) => `/monitoring/traces/${id}`,
      transformResponse: (response: { trace: AgentTrace }) => response.trace,
    }),
    listAuditEntries: builder.query<
      AuditEntry[],
      { user_id?: string; entity_type?: string; limit?: number }
    >({
      query: (params) => ({ url: '/monitoring/audit', params }),
      transformResponse: (response: { entries: AuditEntry[]; total: number }) => response.entries,
      providesTags: ['Audit'],
    }),
  }),
});

export const { useGetStatsQuery, useListTracesQuery, useGetTraceQuery, useListAuditEntriesQuery } =
  monitoringApi;
